// api.tsx
import axios, { type InternalAxiosRequestConfig, type AxiosError } from "axios";
import { getIdTokenFromSession } from "~/lib/utils";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "",
});

// Helper function to check if error is a timeout or network error that might still allow WebSocket processing
const isTimeoutError = (error: any): boolean => {
  if (!error) return false;

  // Check for 504 Gateway Timeout
  if (error.response?.status === 504) {
    return true;
  }

  // Check for timeout error codes
  if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
    return true;
  }

  // Check for network errors (ERR_NETWORK) - these can occur when HTTP times out but WebSocket continues
  // This includes CORS errors that happen during long-running requests
  if (error.code === "ERR_NETWORK" || error.code === "ERR_FAILED") {
    return true;
  }

  // Check for network errors that might indicate timeout
  if (
    error.message?.includes("timeout") ||
    error.message?.includes("504") ||
    error.message?.includes("Network Error")
  ) {
    return true;
  }

  // Check if request was made but no response (timeout scenario)
  // This includes CORS preflight failures that can happen with long requests
  if (error.request && !error.response) {
    return true;
  }

  return false;
};

// Add an interceptor for requests
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig<any>) => {
    // Retrieve the Cognito ID token
    const idToken = await getIdTokenFromSession();
    if (idToken) {
      config.headers["Authorization"] = idToken;
    } else {
      // If no token, don't set empty Authorization header
      // Let the request fail with proper error handling
      console.warn(
        "No authentication token available for request:",
        config.url
      );
    }
    return config;
  },
  (error) => {
    // Handle the error
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    // Check for errors in infos array
    if (response.data?.infos && Array.isArray(response.data.infos)) {
      const errors = response.data.infos.filter((info: any) =>
        info.type === 'ERROR' ||
        info.code === 'HTTP_ERROR' ||
        info.code?.includes('ERROR')
      );
      if (errors.length > 0) {
        const error = new Error(errors[0]?.description || errors[0]?.message || 'API Error');
        (error as any).infos = errors;
        (error as any).response = response;
        return Promise.reject(error);
      }
    }
    
    // Extract returnedObject if present
    if (response.data?.returnedObject !== undefined) {
      response.data = response.data.returnedObject;
    }
    
    return response;
  },
  (error: AxiosError) => {
    // Mark timeout errors for easier handling in catch blocks
    if (isTimeoutError(error)) {
      (error as any).isTimeout = true;
      (error as any).is504 = error.response?.status === 504;
      console.error("API Timeout/504 Error detected:", {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        code: (error as any).code,
        message: error.message,
      });
    }
    return Promise.reject(error);
  }
);
export default api;
